import React, { useMemo } from "react";

/**
 * Two neurons that draw themselves as SVG strokes.
 * Not a photo: each dendrite is a Bezier path animated via stroke-dashoffset,
 * so the branches appear to grow organically from the soma outward.
 */

// Deterministic PRNG so the layout is stable across renders
function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6D2B79F5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Recursive fractal branching → list of Bezier paths with staggered delay
function growBranch(x0, y0, angle, len, depth, maxDepth, rnd, delay, out) {
  if (depth > maxDepth || len < 8) return;
  const cpDist  = len * (0.30 + rnd() * 0.35);
  const cpAngle = angle + (rnd() - 0.5) * 0.55;
  const cpx = x0 + Math.cos(cpAngle) * cpDist;
  const cpy = y0 + Math.sin(cpAngle) * cpDist;
  const x1  = x0 + Math.cos(angle)   * len;
  const y1  = y0 + Math.sin(angle)   * len;
  const chord   = Math.hypot(x1 - x0, y1 - y0);
  const pathLen = chord * 1.08;
  out.push({
    d: `M ${x0.toFixed(1)} ${y0.toFixed(1)} Q ${cpx.toFixed(1)} ${cpy.toFixed(1)} ${x1.toFixed(1)} ${y1.toFixed(1)}`,
    len: pathLen,
    delay,
    strokeWidth: Math.max(0.55, (maxDepth - depth + 1) * 0.85),
    dur: 0.9 + depth * 0.18,
  });
  const nBranches = depth === 0 ? 2 : (rnd() > 0.35 ? 2 : 1);
  const spread    = 0.75 + depth * 0.10;
  for (let i = 0; i < nBranches; i++) {
    const newAngle = angle + (rnd() - 0.5) * spread;
    const shrink   = 0.55 + rnd() * 0.20;
    growBranch(x1, y1, newAngle, len * shrink, depth + 1, maxDepth, rnd, delay + 0.28 + rnd() * 0.18, out);
  }
}

function generateNeuron({ cx, cy, mainAngle, seed, viewport }) {
  const rnd = mulberry32(seed);
  const paths = [];
  // 1) Main trunk toward the synapse (longer, thicker)
  const trunkLen = viewport.mainTrunkLen;
  growBranch(cx, cy, mainAngle, trunkLen, 0, 5, rnd, 0.0, paths);
  // 2) Radial dendrites (skip a sector already covered by the trunk)
  const N = 14;
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2 + rnd() * 0.22;
    // skip the sector already covered by the trunk
    const diff = Math.atan2(Math.sin(a - mainAngle), Math.cos(a - mainAngle));
    if (Math.abs(diff) < 0.55) continue;
    const len   = 110 + rnd() * 160;
    const delay = 0.35 + rnd() * 0.55;
    growBranch(cx, cy, a, len, 0, 4, rnd, delay, paths);
  }
  return paths;
}

const KEYFRAMES = `
@keyframes syn-draw {
  from { stroke-dashoffset: var(--l); opacity: 0.15; }
  10%  { opacity: 1; }
  to   { stroke-dashoffset: 0; opacity: 1; }
}
@keyframes syn-soma-pulse {
  0%, 100% { opacity: 0.55; transform-box: fill-box; transform-origin: center; transform: scale(0.95); }
  50%      { opacity: 1; transform-box: fill-box; transform-origin: center; transform: scale(1.05); }
}
@keyframes syn-soma-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
`;

export default function AnimatedNeurons({ startDelay = 0.6, className = "", style }) {
  // Viewport 1920x800, left soma at (280,400) reaches right; right soma at (1640,400) reaches left
  const vp = { w: 1920, h: 800, mainTrunkLen: 620 };
  const { left, right } = useMemo(() => ({
    left:  generateNeuron({ cx: 280,  cy: 400, mainAngle: 0,        seed: 7,  viewport: vp }),
    right: generateNeuron({ cx: 1640, cy: 400, mainAngle: Math.PI,  seed: 91, viewport: vp }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  return (
    <div className={`relative w-full h-full ${className}`} style={style}>
      <style>{KEYFRAMES}</style>
      <svg viewBox={`0 0 ${vp.w} ${vp.h}`} preserveAspectRatio="xMidYMid slice"
           className="absolute inset-0 h-full w-full"
           data-testid="animated-neurons">
        <defs>
          <linearGradient id="strokeOrange" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"  stopColor="#FBBF24" stopOpacity="1" />
            <stop offset="55%" stopColor="#F97316" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#FDE68A" stopOpacity="0.10" />
          </linearGradient>
          <linearGradient id="strokeCyan" x1="1" y1="0" x2="0" y2="0">
            <stop offset="0%"  stopColor="#67E8F9" stopOpacity="1" />
            <stop offset="55%" stopColor="#0891B2" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#22D3EE" stopOpacity="0.10" />
          </linearGradient>
          <radialGradient id="somaOrange" cx="50%" cy="50%">
            <stop offset="0%"  stopColor="#FEF3C7" stopOpacity="1" />
            <stop offset="30%" stopColor="#F59E0B" stopOpacity="0.85" />
            <stop offset="70%" stopColor="#B45309" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#F97316" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="somaCyan" cx="50%" cy="50%">
            <stop offset="0%"  stopColor="#ECFEFF" stopOpacity="1" />
            <stop offset="30%" stopColor="#22D3EE" stopOpacity="0.85" />
            <stop offset="70%" stopColor="#0E7490" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#22D3EE" stopOpacity="0" />
          </radialGradient>
          <filter id="softGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="1.4" result="b1" />
            <feMerge>
              <feMergeNode in="b1"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* LEFT (orange) dendrites */}
        <g filter="url(#softGlow)">
          {left.map((p, i) => (
            <path key={`L${i}`} d={p.d}
                  stroke="url(#strokeOrange)" strokeWidth={p.strokeWidth}
                  fill="none" strokeLinecap="round" strokeLinejoin="round"
                  style={{
                    "--l": p.len,
                    strokeDasharray: p.len,
                    strokeDashoffset: p.len,
                    opacity: 0,
                    animation: `syn-draw ${p.dur.toFixed(2)}s cubic-bezier(0.22,1,0.36,1) ${(startDelay + p.delay).toFixed(2)}s forwards`,
                  }} />
          ))}
        </g>

        {/* RIGHT (cyan) dendrites */}
        <g filter="url(#softGlow)">
          {right.map((p, i) => (
            <path key={`R${i}`} d={p.d}
                  stroke="url(#strokeCyan)" strokeWidth={p.strokeWidth}
                  fill="none" strokeLinecap="round" strokeLinejoin="round"
                  style={{
                    "--l": p.len,
                    strokeDasharray: p.len,
                    strokeDashoffset: p.len,
                    opacity: 0,
                    animation: `syn-draw ${p.dur.toFixed(2)}s cubic-bezier(0.22,1,0.36,1) ${(startDelay + p.delay).toFixed(2)}s forwards`,
                  }} />
          ))}
        </g>

        {/* Soma glows (appear once trunk is well underway) */}
        <g style={{ opacity: 0, animation: `syn-soma-in 0.9s ease-out ${(startDelay + 0.4).toFixed(2)}s forwards` }}>
          <circle cx="280" cy="400" r="120" fill="url(#somaOrange)"
                  style={{ animation: `syn-soma-pulse 2.4s ease-in-out ${(startDelay + 1.4).toFixed(2)}s infinite` }} />
          <circle cx="280" cy="400" r="10" fill="#FDE68A" opacity="0.95" />
        </g>
        <g style={{ opacity: 0, animation: `syn-soma-in 0.9s ease-out ${(startDelay + 0.6).toFixed(2)}s forwards` }}>
          <circle cx="1640" cy="400" r="120" fill="url(#somaCyan)"
                  style={{ animation: `syn-soma-pulse 2.4s ease-in-out ${(startDelay + 1.6).toFixed(2)}s infinite` }} />
          <circle cx="1640" cy="400" r="10" fill="#ECFEFF" opacity="0.95" />
        </g>
      </svg>
    </div>
  );
}
